import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ColumnDef, Row } from "@tanstack/react-table";
import { classNames } from "@/utils/Classnames";
import { useGetFullContainers } from "@/services/LogisticsCoordinator";
import { FullContainerOverview } from "@/types";
import { SubHeaderTitle } from "../shared";
import { SubHeaderBase } from "@/components/molecules/SubHeader";
import dayjs from "dayjs";
import DataTable from "@/components/molecules/DataTable";
import Pagination from "@/components/molecules/Pagination";
import SortableHeader from "@/components/molecules/SortableHeader";
import Text from "@/components/atoms/Text";
import styles from "./styles.module.css";
import shared from "../styles.module.css";
import CollectionDetail from "./CollectionDetail";

type SortId = "site" | "waste" | "reported";
interface SortState {
  id: SortId;
  desc: boolean;
}

const Collections = () => {
  const { t } = useTranslation();

  const { data: containerData, isLoading } = useGetFullContainers();

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get("containerId")) || null;

  const [sorting, setSorting] = useState<SortState | null>({ id: "reported", desc: true });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  const containers = containerData?.containers ?? [];
  const siteCount = containerData?.siteCount ?? 0;
  const selectedContainer = containers.find((c) => c.containerId === selectedId) ?? containers[0];

  const toggleSort = (id: SortId) => {
    setSorting((prev) => (prev?.id === id ? { id, desc: !prev.desc } : { id, desc: false }));
    setPage(1);
  };

  // Sort the full list before paginating — DataTable only sorts the rows it's handed.
  // With no active sort, keep the backend's order (date ascending).
  const sortedContainers = useMemo(() => {
    if (!sorting) return containers;
    const compare = (a: FullContainerOverview, b: FullContainerOverview) => {
      switch (sorting.id) {
        case "site":
          return a.siteName.localeCompare(b.siteName);
        case "waste":
          return a.wasteTypeName.localeCompare(b.wasteTypeName);
        case "reported":
          return dayjs(a.reported).valueOf() - dayjs(b.reported).valueOf();
      }
    };
    return [...containers].sort((a, b) => (sorting.desc ? -compare(a, b) : compare(a, b)));
  }, [containers, sorting]);

  const pageData = useMemo(
    () => sortedContainers.slice((page - 1) * rowsPerPage, page * rowsPerPage),
    [sortedContainers, page, rowsPerPage]
  );

  // A deep-linked container may sit on a later page — jump to it once the data has loaded.
  useEffect(() => {
    if (!selectedId || !sortedContainers.length) return;
    const index = sortedContainers.findIndex((c) => c.containerId === selectedId);
    if (index >= 0) setPage(Math.floor(index / rowsPerPage) + 1);
  }, [selectedId, sortedContainers, rowsPerPage]);

  const columns = useMemo<ColumnDef<FullContainerOverview>[]>(() => {
    const sortable = (id: SortId) => ({
      getIsSorted: () => (sorting?.id === id ? (sorting.desc ? "desc" : "asc") : false),
      getToggleSortingHandler: () => () => toggleSort(id),
    });
    return [
      {
        id: "site",
        size: 320,
        header: () => <SortableHeader column={sortable("site")} label={t("containers:columns.site")} />,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className={styles.siteCell}>
              <div style={{ minWidth: 0 }}>
                <div className={styles.siteName}>
                  <Text span size="sm" strong nowrap>
                    {c.siteName}
                  </Text>
                  {c.isNew && <span className={styles.newTag}>{t("containers:new")}</span>}
                </div>
                <Text span={false} size="xs" color="tertiary" className={styles.addr}>
                  {c.siteAddress}
                </Text>
              </div>
            </div>
          );
        },
      },
      {
        id: "waste",
        size: 200,
        header: () => <SortableHeader column={sortable("waste")} label={t("containers:columns.wasteType")} />,
        cell: ({ row }) => (
          <div className={styles.wasteCell}>
            {/* TODO: waste-type tile (abbreviation) — no abbr field from the API yet. */}
            {/* <TypeTile abbr={row.original.abbr} size={30} /> */}
            <Text span size="sm">
              {row.original.wasteTypeName}
            </Text>
          </div>
        ),
      },
      {
        id: "reported",
        size: 160,
        header: () => <SortableHeader column={sortable("reported")} label={t("containers:columns.reported")} />,
        cell: ({ row }) => (
          <div className={styles.reportedRel}>
            <Text span display="technical" size="sm">
              {dayjs(row.original.reported).format("DD MMM")}
            </Text>
            <Text span display="technical" size="xs" color="secondary">
              {dayjs(row.original.reported).format("HH:mm")}
            </Text>
          </div>
        ),
      },
    ];
  }, [t, sorting]);

  return (
    <div className={shared.page}>
      <SubHeaderBase variant="primary">
        <SubHeaderTitle
          heading={t("containers:collections.title")}
          sub={t("containers:collections.subtitle", { count: containers.length, sites: siteCount })}
        />
      </SubHeaderBase>

      <main className={shared.layout}>
        <div>
          <section className={styles.masterCard}>
            <DataTable
              data={pageData}
              columns={columns}
              isLoading={isLoading}
              actionable={false}
              showPagination={false}
              useServerSidePagination
              onRowClick={(row: Row<FullContainerOverview>) =>
                setSearchParams({ containerId: String(row.original.containerId) }, { replace: true })
              }
              rowClassName={(c) =>
                classNames(c.containerId === selectedContainer?.containerId ? styles.selectedRow : "")
              }
            />
          </section>

          <Pagination
            activePage={page}
            totalItems={sortedContainers.length}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(value) => {
              setRowsPerPage(Number(value));
              setPage(1);
            }}
            onPageChange={setPage}
          />
        </div>

        {selectedContainer && <CollectionDetail key={selectedContainer.containerId} container={selectedContainer} />}
      </main>
    </div>
  );
};

export default Collections;
